import { useEffect, useState } from "react";
import { Bug, Download, Copy, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { sessionLogger } from "@/utils/sessionLogger";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

const ExportLogButton = () => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(sessionLogger.count());
  const [description, setDescription] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    const unsub = sessionLogger.subscribe(() => setCount(sessionLogger.count()));
    return () => {
      unsub();
    };
  }, []);

  const buildHeader = () => ({
    Route: window.location.pathname + window.location.search,
    Origin: window.location.origin,
    User: user?.email || "anonymous",
    Viewport: `${window.innerWidth}x${window.innerHeight}`,
    UserAgent: navigator.userAgent,
    Description: description.trim() || "(none)",
  });

  const handleDownload = () => {
    const text = sessionLogger.exportAsText(buildHeader());
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `ledlogic-session-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Log downloaded", description: "Send this file to support so we can help." });
  };

  const handleCopy = async () => {
    const text = sessionLogger.exportAsText(buildHeader());
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied to clipboard", description: "Paste into an email or chat." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Use the Download button instead.",
        variant: "destructive",
      });
    }
  };

  const handleClear = () => {
    sessionLogger.clear();
    toast({ title: "Session log cleared" });
  };

  return (
    <>
      <button
        type="button"
        data-session-logger-ignore="true"
        onClick={() => setOpen(true)}
        aria-label="Report an issue"
        title="Report an issue"
        className="hover:text-white transition-colors min-h-[44px] sm:min-h-0 flex items-center gap-1.5 px-2"
      >
        <Bug className="h-3.5 w-3.5" />
        <span>Report issue</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an Issue</DialogTitle>
            <DialogDescription>
              Export a log of this session ({count} {count === 1 ? "entry" : "entries"}) so we can
              diagnose the problem. Nothing is sent automatically — the file stays on your device
              until you share it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-sm text-white/80" htmlFor="issue-description">
              What went wrong? (optional)
            </label>
            <Textarea
              id="issue-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe what you were trying to do and what happened..."
              rows={4}
              className="bg-slate-800/60 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleClear}
              data-session-logger-ignore="true"
              className="sm:mr-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            <Button variant="outline" onClick={handleCopy} data-session-logger-ignore="true">
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button onClick={handleDownload} data-session-logger-ignore="true">
              <Download className="h-4 w-4 mr-2" />
              Download Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExportLogButton;
